import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { bookingAPI } from '../../api/bookings';
import { businessAPI } from '../../api/businesses';
import { useAuth } from '../../contexts/AuthContext';

const AnalyticsDashboard = () => {
  const { user, isBusinessOwner } = useAuth();
  const [period, setPeriod] = useState('month');
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState([]);
  const [popularServices, setPopularServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      let businessId;
      if (isBusinessOwner) {
        const businessRes = await businessAPI.getMyBusiness();
        businessId = businessRes.data.data.id;
      }

      const statsRes = await bookingAPI.getBusinessStats(businessId, period);
      setStats(statsRes.data.data);

      // Mock trends data - In production, fetch from API
      setTrends([
        { date: 'Week 1', bookings: 12, revenue: 360 },
        { date: 'Week 2', bookings: 18, revenue: 540 },
        { date: 'Week 3', bookings: 15, revenue: 450 },
        { date: 'Week 4', bookings: 22, revenue: 660 },
      ]);

      setPopularServices([
        { name: 'Haircut', value: 45 },
        { name: 'Color', value: 30 },
        { name: 'Styling', value: 25 },
      ]);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

  if (loading) return <div className="text-center py-8">Loading analytics...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Analytics Dashboard</h1>
          <p className="text-gray-600">Track your business performance</p>
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="input w-32"
        >
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="quarter">This Quarter</option>
          <option value="year">This Year</option>
        </select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <p className="text-sm text-gray-600">Total Bookings</p>
          <p className="text-3xl font-bold text-gray-900">{stats?.total_bookings || 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <p className="text-sm text-gray-600">Completed</p>
          <p className="text-3xl font-bold text-green-600">{stats?.completed_bookings || 0}</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <p className="text-sm text-gray-600">Total Revenue</p>
          <p className="text-3xl font-bold text-primary-600">${stats?.total_revenue || 0}</p>
        </div>
      </div>

      {/* Booking Trends Chart */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold mb-4">Booking Trends</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={trends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="bookings" stroke="#3b82f6" name="Bookings" />
            <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#10b981" name="Revenue ($)" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Popular Services & Revenue Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4">Popular Services</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={popularServices}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#3b82f6" name="Bookings" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4">Service Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={popularServices}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {popularServices.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Business Insights */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg shadow-md p-6 text-white">
        <h2 className="text-xl font-semibold mb-2">Business Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <p className="text-sm opacity-90">Peak Hour</p>
            <p className="text-2xl font-bold">2:00 PM - 4:00 PM</p>
          </div>
          <div>
            <p className="text-sm opacity-90">Best Day</p>
            <p className="text-2xl font-bold">Saturday</p>
          </div>
          <div>
            <p className="text-sm opacity-90">Customer Retention</p>
            <p className="text-2xl font-bold">68%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;